import { Flex, Popover, SimpleGrid, Text } from "@mantine/core";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useGenerateJwtToken } from "../../api/hooks/auth/useGenerateJwtToken.ts";
import SvgArrowBadgeLeft from "../../components/Icons/ArrowBadgeLeft.tsx";
import SvgArrowBadgeRight from "../../components/Icons/ArrowBadgeRight.tsx";
import SvgCRM from "../../components/Icons/CRM.tsx";
import { useNavigationLinks } from "../../components/Layout/hooks/useNavigationLinks.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import SidebarItem from "../../components/Layout/Sidebar/SidebarItem/SidebarItem.tsx";
import {
  NavigationLink,
  NavigationNestedLink,
} from "../../components/Layout/types.ts";
import appConfig from "../../config.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useStyles } from "./MainPage.styles.ts";

export const MainPage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const linkIconSize = 50;

  const [openedLink, setOpenedLink] = useState<string | null>();

  const [, generateJwtToken] = useMutationNotificationWrapper(
    useGenerateJwtToken(),
    { success: { show: false }, error: { show: false } }
  );

  const handleOpenLink = useCallback(
    (link: NavigationLink | NavigationNestedLink) => {
      if (link.url) {
        navigate(link.url);
      } else {
        setOpenedLink(link.shortLabel ?? link.label);
      }
    },
    [navigate]
  );

  const renderMainLink = useCallback(
    (link: NavigationLink, onOpen?: () => void) => {
      const label = link.shortLabel ?? link.label;
      return (
        <Flex
          pos="relative"
          direction="column"
          p={20}
          gap={20}
          align="center"
          justify="center"
          mih={150}
          className={classes.linkWrapper}
          onClick={() => (onOpen ? onOpen() : handleOpenLink(link))}
        >
          {link.nestedLinks?.length && (
            <Flex pos="absolute" top={10} right={10}>
              {openedLink === label ? (
                <SvgArrowBadgeLeft />
              ) : (
                <SvgArrowBadgeRight />
              )}
            </Flex>
          )}
          {link.icon}
          <Text align="center">{label}</Text>
        </Flex>
      );
    },
    [classes.linkWrapper, handleOpenLink, openedLink]
  );

  const renderNestedLink = useCallback(
    (nestedLink: NavigationNestedLink, index: number) => {
      return (
        <SidebarItem
          key={index}
          item={nestedLink}
          isOpen={true}
          onClick={() => handleOpenLink(nestedLink)}
        />
      );
    },
    [handleOpenLink]
  );

  const renderer = useCallback(
    (link: NavigationLink, index: number) => {
      const nestedLinks = link.nestedLinks?.filter(
        ({ show }) => show !== false
      );
      return (
        <Popover
          key={index}
          disabled={!nestedLinks?.length}
          position="right-start"
          withArrow
          width="auto"
          arrowPosition="side"
          shadow="xs"
          styles={{ dropdown: { maxHeight: "75%", overflow: "scroll" } }}
          onOpen={() => handleOpenLink(link)}
          onClose={() => setOpenedLink(null)}
        >
          <Popover.Target>{renderMainLink(link)}</Popover.Target>
          <Popover.Dropdown className={classes.nestedLinkWrapper}>
            {nestedLinks?.map(renderNestedLink)}
          </Popover.Dropdown>
        </Popover>
      );
    },
    [
      classes.nestedLinkWrapper,
      handleOpenLink,
      renderMainLink,
      renderNestedLink,
    ]
  );

  const links = useNavigationLinks({ renderer, iconSize: linkIconSize });

  const handleOpenCrm = useCallback(() => {
    generateJwtToken({}).then((data) => {
      const token = data.data?.generateJwtToken;
      if (!token) {
        return;
      }
      window.open(
        `${appConfig.crmUrl}?token=${token}`,
        "_blank",
        "noopener,noreferrer"
      );
    });
  }, [generateJwtToken]);

  return (
    <Layout>
      <Flex
        w="100%"
        h="100%"
        pr="20vw"
        pl="20vw"
        align="center"
        justify="center"
      >
        <Flex direction="column" align="center" justify="center">
          <SimpleGrid
            cols={4}
            spacing={50}
            w="100%"
            breakpoints={[
              { minWidth: "sm", cols: 2 },
              { minWidth: "md", cols: 3 },
              { minWidth: "lg", cols: 4 },
            ]}
          >
            {links}
            {renderMainLink(
              {
                label: "CRM",
                icon: <SvgCRM width={linkIconSize} height={linkIconSize} />,
                url: "",
              },
              handleOpenCrm
            )}
          </SimpleGrid>
        </Flex>
      </Flex>
    </Layout>
  );
};
